/**
 * Логика полов: покрытие по материалу или id, допустимые укладки, типовые настройки
 * и итоговый вид пола комнаты (настройки слоя поверх каталога).
 */
import type { RoomFloor } from "@/shared/plan";
import { floorTextures, wallTextures } from "../finish/library";
import type { FinishTexture, FloorTextureKind } from "../finish/types";
import { floorSection } from "./catalog";
import { DEFAULT_FLOOR_LAYOUT, DEFAULT_FLOOR_MATERIAL, FLOOR_LAYOUTS } from "./constants";
import {
	FloorMaterial,
	type FloorCatalogItem,
	type FloorLayout,
	type FloorLayoutInfo,
	type FloorLook,
	type FloorTexture,
} from "./types";

/** Покрытие из каталога по материалу; неизвестный материал — первое покрытие, чтобы пол не пропал. */
export const getFloorMaterial = (material: FloorMaterial | undefined): FloorCatalogItem =>
	floorSection.items.find((item) => item.material === (material ?? DEFAULT_FLOOR_MATERIAL)) ??
	floorSection.items[0];

/** Покрытие по строковому id из ссылки каталога (`CatalogRef.item`). */
export const floorItemById = (id: string): FloorCatalogItem | null =>
	floorSection.items.find((item) => item.id === id) ?? null;

export const getFloorLayout = (id: FloorLayout | undefined): FloorLayoutInfo =>
	FLOOR_LAYOUTS.find((layout) => layout.id === (id ?? DEFAULT_FLOOR_LAYOUT)) ?? FLOOR_LAYOUTS[0];

/** Укладки, которые каталог разрешает этому покрытию, с подписями и в порядке справочника. */
export const allowedFloorLayouts = (material: FloorMaterial): FloorLayoutInfo[] => {
	const allowed = getFloorMaterial(material).allowedLayouts;
	return FLOOR_LAYOUTS.filter((layout) => allowed.includes(layout.id));
};

/** Укладка, которую можно поставить этому покрытию: запрошенная, если разрешена, иначе типовая. */
export const resolveFloorLayout = (material: FloorMaterial, wanted?: FloorLayout): FloorLayout => {
	const item = getFloorMaterial(material);
	return wanted !== undefined && item.allowedLayouts.includes(wanted) ? wanted : item.defaultLayout;
};

/** Типовые настройки покрытия из каталога — с них начинается пол новой комнаты. */
export const getFloorPreset = (
	material: FloorMaterial,
): Omit<RoomFloor, "id" | "roomId" | "material"> => {
	const item = getFloorMaterial(material);
	return {
		layout: item.defaultLayout,
		length: item.tile.length,
		width: item.tile.width,
		gap: item.tile.gap,
		raise: 0,
		rotation: 0,
		offsetX: 0,
		offsetY: 0,
		tileColor: item.color,
		seamColor: item.seamColor,
	};
};

/** Картинки пола по покрытию и поиск по id — от текущей библиотеки, как у отделки стен. */
let index: {
	wall: readonly FinishTexture[];
	floor: readonly FinishTexture<FloorTextureKind>[];
	byMaterial: Record<FloorMaterial, readonly FloorTexture[]>;
	byId: Map<string, FloorTexture>;
} | null = null;

const floorIndex = () => {
	const wall = wallTextures();
	const floor = floorTextures();
	if (index?.wall !== wall || index.floor !== floor) {
		// Плитка из библиотеки стен — её же кладут на пол
		const tiles = wall.filter((t): t is FinishTexture<"tile"> => t.kind === "tile");
		const ofKind = (...kinds: FloorTextureKind[]) => floor.filter((t) => kinds.includes(t.kind));
		index = {
			wall,
			floor,
			// Плитке — плитка, ламинату — доска и паркет, кварцвинилу — доска, линолеуму —
			// его полотна и доска. Обоев и панелей у пола нет
			byMaterial: {
				[FloorMaterial.Tile]: tiles,
				[FloorMaterial.Laminate]: ofKind("wood", "parquet"),
				[FloorMaterial.QuartzVinyl]: ofKind("wood"),
				[FloorMaterial.Linoleum]: ofKind("linoleum", "wood"),
			},
			byId: new Map<string, FloorTexture>([...floor, ...tiles].map((t) => [t.id, t])),
		};
	}
	return index;
};

/** Какие картинки подходят покрытию. */
export const floorTexturesFor = (material: FloorMaterial): readonly FloorTexture[] =>
	floorIndex().byMaterial[material];

/** Текстура пола по id из записи комнаты; неизвестная (удалили из библиотеки) — нет. */
export const floorTextureById = (id: string | undefined): FloorTexture | undefined =>
	id === undefined ? undefined : floorIndex().byId.get(id);

/**
 * Картинка — готовый рисунок всего покрытия (паркет, полотно линолеума): кладётся как есть,
 * укладка и швы к ней не применяются. Доска и плитка — рисунок одной планки или плитки.
 */
export const isWholeFloorImage = (texture: FloorTexture | undefined): boolean =>
	texture?.kind === "parquet" || texture?.kind === "linoleum";

/**
 * Итоговый вид пола: что пользователь поменял — из слоя, остальное — из каталога.
 * Без покрытия (`floor` не задан) отдаёт типовую плитку — для тех мест, где нужно
 * хоть что-то нарисовать.
 */
export const floorLook = (floor?: RoomFloor): FloorLook => {
	const material = floor?.material ?? DEFAULT_FLOOR_MATERIAL;
	const item = getFloorMaterial(material);
	const texture = floorTextureById(floor?.texture);
	return {
		material,
		layout: resolveFloorLayout(material, floor?.layout),
		length: floor?.length ?? item.tile.length,
		width: floor?.width ?? item.tile.width,
		gap: floor?.gap ?? item.tile.gap,
		// У картинки цвет — её средний: им пол на чертеже и пока картинка грузится в 3D
		color: texture?.color ?? floor?.tileColor ?? item.color,
		seamColor: floor?.seamColor ?? item.seamColor,
		raise: floor?.raise ?? 0,
		rotation: floor?.rotation ?? 0,
		offsetX: floor?.offsetX ?? 0,
		offsetY: floor?.offsetY ?? 0,
		...(texture && { texture }),
	};
};
